
import { TICK_HZ, TICK_MS } from "../sim/movement";
import { isAttackable } from "./target";
import type { ClipName } from "../render/creaturemesh";

/** `CombatEvent.flags`, mirroring `metin3/net/schema.py`. */
export const COMBAT_KILLED = 1 << 0;
export const COMBAT_CRITICAL = 1 << 1;
export const COMBAT_MISSED = 1 << 2;
export const COMBAT_BLOCKED = 1 << 3;
export const COMBAT_NORMAL_ATTACK = 1 << 4;
/** The blow left the target stunned, not yet dead (`char_battle.cpp:363-400`). */
export const COMBAT_STUNNED = 1 << 5;

export function canUseSkill(dead: boolean, ownStallOpen: boolean): boolean {
  return !dead && !ownStallOpen;
}

/** One resolved damage event, in the client's own vocabulary. */
export interface CombatResolution {
  readonly attackerId: number;
  readonly targetId: number;
  readonly damage: number;
  readonly flags: number;
  /** The skill vnum that dealt the blow, 0 for an ordinary swing --
   *  `CombatEvent.skill` (lane PN). Optional so a sink built before the field
   *  existed still compiles; `render/framefx.ts` is the reader. */
  readonly skill?: number;
  /** `CombatEvent.combo_motion`: the n of the `MOTION_COMBO_ATTACK_n` the
   *  attacker's swing plays, 0 or absent for a blow that is no combo step. */
  readonly comboMotion?: number;
}

/** One `CreateFly` frame, in the client's own vocabulary
 *  (`char_battle.cpp:88-98`; the type indexes
 *  `playersettingmodule.py:419-434`). */
export interface FlyLaunch {
  readonly fly: number;
  readonly fromId: number;
  readonly toId: number;
}

export function shouldPresentRemoteNormalAttack(
  event: CombatResolution,
  selfId: number,
): boolean {
  return event.attackerId !== selfId && (event.flags & COMBAT_NORMAL_ATTACK) !== 0;
}

/** The flag word, unpacked. Bits are independent: a blocked hit can still kill. */
export interface CombatOutcome {
  readonly killed: boolean;
  readonly critical: boolean;
  readonly missed: boolean;
  readonly blocked: boolean;
  readonly stunned: boolean;
}

/** Whether this client draws the target going down: on its death, or on the
 *  stun of this client's own character, which `RecvStunPacket` plays as
 *  `Die` while every other client plays `Stun`
 *  (`PythonNetworkStreamPhaseGame.cpp:1512-1521`). */
export function fallsHere(outcome: CombatOutcome, targetId: number, selfId: number): boolean {
  return outcome.killed || (outcome.stunned && targetId === selfId);
}

/** Whether this client hangs the stun on the target: the `Stun` half of
 *  `RecvStunPacket`, played on every body but this client's own character
 *  (`PythonNetworkStreamPhaseGame.cpp:1512-1521`). */
export function stunsHere(outcome: CombatOutcome, targetId: number, selfId: number): boolean {
  return outcome.stunned && targetId !== selfId;
}

export function decodeCombatFlags(flags: number): CombatOutcome {
  return {
    killed: (flags & COMBAT_KILLED) !== 0,
    critical: (flags & COMBAT_CRITICAL) !== 0,
    missed: (flags & COMBAT_MISSED) !== 0,
    blocked: (flags & COMBAT_BLOCKED) !== 0,
    stunned: (flags & COMBAT_STUNNED) !== 0,
  };
}

export type FloaterStyle = "hit" | "crit" | "miss" | "block";

export function floaterStyleFor(outcome: CombatOutcome): FloaterStyle {
  if (outcome.missed) return "miss";
  if (outcome.blocked) return "block";
  if (outcome.critical) return "crit";
  return "hit";
}

/** The text on the floater. Misses and blocks carry a word, not a zero. */
export function floaterTextFor(damage: number, outcome: CombatOutcome): string {
  if (outcome.missed) return "miss";
  if (outcome.blocked && damage === 0) return "block";
  if (outcome.critical) return `${damage}!`;
  return String(damage);
}

// ------------------------------------------------------------------- floaters

export const FLOATER_LIFETIME_MS = 1_100;
/** Long enough to read a three-digit number, short enough that a fast weapon does
 *  not stack six of them into an unreadable column. */

export const MAX_FLOATERS = 24;
/** Hard cap, independent of the lifetime. The lifetime alone bounds a *normal*
 *  fight; the cap bounds an abnormal one -- a server bug, a hundred-monster pull,
 *  a tab that was backgrounded so no frame ran to prune. */

export const FLOATER_RISE_PX = 46;
export const FLOATER_STACK_PX = 15;
export const FLOATER_STACK_MAX = 4;
/** Simultaneous floaters on one entity are offset so they do not overprint, but
 *  the offset wraps: without the wrap a sustained fight walks them off screen. */

export const FLOATER_FADE_START = 0.55;

export interface Floater {
  readonly key: number;
  readonly entityId: number;
  readonly attackerId: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly text: string;
  readonly style: FloaterStyle;
  /** True when the local player is the one taking the damage. */
  readonly incoming: boolean;
  readonly bornMs: number;
  readonly stack: number;
}

/** Everything about a floater except when it was born and where it stacks. */
export interface FloaterSpec {
  readonly entityId: number;
  readonly attackerId: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly text: string;
  readonly style: FloaterStyle;
  readonly incoming: boolean;
}

/** 0 at birth, 1 at death. Clamped, so a paused tab does not produce a negative. */
export function floaterPhase(floater: Floater, nowMs: number): number {
  const age = (nowMs - floater.bornMs) / FLOATER_LIFETIME_MS;
  return age <= 0 ? 0 : age >= 1 ? 1 : age;
}

/** Pixels above the entity. Rises fast then eases, which reads as thrown rather
 *  than lifted; a linear rise looks mechanical at these durations. */
export function floaterOffsetPx(floater: Floater, nowMs: number): number {
  const phase = floaterPhase(floater, nowMs);
  const eased = 1 - (1 - phase) * (1 - phase);
  return eased * FLOATER_RISE_PX + floater.stack * FLOATER_STACK_PX;
}

/** Opaque for the first half -- the number must be legible before it starts to
 *  go -- then a linear fade to nothing. */
export function floaterAlpha(floater: Floater, nowMs: number): number {
  const phase = floaterPhase(floater, nowMs);
  if (phase <= FLOATER_FADE_START) return 1;
  return Math.max(0, 1 - (phase - FLOATER_FADE_START) / (1 - FLOATER_FADE_START));
}

/** The live floaters, oldest first. Bounded by both lifetime and count. */
export class FloaterField {
  private items: Floater[] = [];
  private nextKey = 0;

  spawn(spec: FloaterSpec, nowMs: number): Floater {
    this.prune(nowMs);
    // Stack against what is already floating on *this* entity, so two monsters
    // hit in the same tick each start at their own baseline.
    let onEntity = 0;
    for (const item of this.items) if (item.entityId === spec.entityId) onEntity += 1;

    const floater: Floater = {
      ...spec,
      key: this.nextKey,
      bornMs: nowMs,
      stack: onEntity % FLOATER_STACK_MAX,
    };
    this.nextKey += 1;
    this.items.push(floater);
    // Drop the oldest, not the newest: the number that just appeared is the one
    // the player is looking for.
    while (this.items.length > MAX_FLOATERS) this.items.shift();
    return floater;
  }

  prune(nowMs: number): void {
    this.items = this.items.filter((item) => nowMs - item.bornMs < FLOATER_LIFETIME_MS);
  }

  get size(): number {
    return this.items.length;
  }

  /** Read-only view. Call `prune` first; drawing must not mutate. */
  get all(): readonly Floater[] {
    return this.items;
  }

  clear(): void {
    this.items = [];
  }
}

// --------------------------------------------------------------------- impacts

export interface Impact {
  readonly key: number;
  /** The victim. */
  readonly entityId: number;
  readonly attackerId: number;
  readonly xMm: number;
  readonly yMm: number;
  /** Never `"miss"`: an impact is a blow that connected. */
  readonly style: FloaterStyle;
  readonly bornMs: number;
  readonly local: boolean;
  /** The skill vnum that dealt the blow; see `ImpactSpec.skill`. */
  readonly skill?: number;
}

/** Everything about an impact except when it happened, its key, and who raised
 *  it. */
export interface ImpactSpec {
  readonly entityId: number;
  readonly attackerId: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly style: FloaterStyle;
  /** The skill vnum that dealt the blow, 0 or absent for an ordinary swing:
   *  `__ProcessDataAttackSuccess`'s `uiSkill` (`ActorInstanceBattle.cpp:605`). */
  readonly skill?: number;
}

export const IMPACT_KEEP_MS = 600;

/** A cap, for `MAX_FLOATERS`' reason: the lifetime bounds an ordinary fight and
 *  the cap bounds a pathological one. */
export const MAX_IMPACTS = 24;

// ------------------------------------------------------------------ hit frames

/** One armed hit frame: whose motion, and the instant it reaches the frame that
 *  can strike. */
export interface HitFrame {
  readonly attackerId: number;
  readonly dueMs: number;
}

export class HitFrameQueue {
  private readonly pending = new Map<number, HitFrame>();

  arm(attackerId: number, dueMs: number): void {
    this.pending.set(attackerId, { attackerId, dueMs });
  }

  /** Every hit frame that has arrived, removed as it is handed back -- spent
   *  once, like the original's `m_HitDataMap` entry for a victim a hit datum
   *  has already reached (`ActorInstanceCollisionDetection.cpp:364-376`). */
  due(nowMs: number): readonly HitFrame[] {
    const ready: HitFrame[] = [];
    for (const hit of this.pending.values()) {
      if (nowMs >= hit.dueMs) ready.push(hit);
    }
    for (const hit of ready) this.pending.delete(hit.attackerId);
    return ready;
  }

  /** Called on a reconnect: entity ids are reissued, so nothing still
   *  pending belongs to this world. See `main.ts`'s `enteredWorld`. */
  clear(): void {
    this.pending.clear();
  }
}

// --------------------------------------------------------------------- flashes

export const HIT_FLASH_MS = 220;

/** 1 at the instant of the hit, 0 once it has faded. Outside the window, zero --
 *  never negative, which would invert the tint it drives. */
export function flashIntensity(hitAtMs: number, nowMs: number): number {
  const age = nowMs - hitAtMs;
  if (age < 0 || age >= HIT_FLASH_MS) return 0;
  return 1 - age / HIT_FLASH_MS;
}

export class FlashField {
  private readonly hits = new Map<number, number>();

  hit(entityId: number, nowMs: number): void {
    this.hits.set(entityId, nowMs);
  }

  intensity(entityId: number, nowMs: number): number {
    const at = this.hits.get(entityId);
    return at === undefined ? 0 : flashIntensity(at, nowMs);
  }

  prune(nowMs: number): void {
    for (const [id, at] of this.hits) {
      if (nowMs - at >= HIT_FLASH_MS) this.hits.delete(id);
    }
  }

  get size(): number {
    return this.hits.size;
  }

  clear(): void {
    this.hits.clear();
  }
}

// ---------------------------------------------------------------------- deaths

export const DEATH_EFFECT_MS = 900;
export const MAX_DEATHS = 12;

export const SWING_KEEP_MS = 1_000;

/** A cap for the same reason `MAX_DEATHS` has one: a player standing in a pack
 *  of wolves is the subject of a great many swings a second, and the feed is
 *  read every frame. */
export const MAX_SWINGS = 24;

export interface DeathMark {
  readonly key: number;
  readonly entityId: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly bornMs: number;
}

export interface Swing {
  readonly key: number;
  readonly entityId: number;
  readonly bornMs: number;
  /** The combo step this client decided its own swing plays, where it decided one. */
  readonly step?: number;
  /** The clip that step is in this client's combo type, where it decided one. */
  readonly clip?: ClipName;
  /** The combo motion the server relayed for somebody else's swing -- see
   *  `CombatResolution.comboMotion`. */
  readonly motion?: number;
}

export function deathPhase(mark: DeathMark, nowMs: number): number {
  const age = (nowMs - mark.bornMs) / DEATH_EFFECT_MS;
  return age <= 0 ? 0 : age >= 1 ? 1 : age;
}

// ------------------------------------------------------------------------ casts

export const CAST_KEEP_MS = 1_000;

export const MAX_CASTS = 12;
/** A cap for `MAX_DEATHS`' reason. Cooldowns make a flood of casts impossible
 *  in a correct session, which is exactly why the cap is here: it bounds the
 *  incorrect one. */

export interface SkillCast {
  readonly key: number;
  readonly casterId: number;
  readonly vnum: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly headingRad: number;
  readonly bornMs: number;
}

/** Everything about a cast except when it happened and its key. */
export interface SkillCastSpec {
  readonly casterId: number;
  readonly vnum: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly headingRad?: number;
}

// -------------------------------------------------------------- canned effects

export const SPECIAL_KEEP_MS = 1_000;
/** `CAST_KEEP_MS`' window and `CAST_KEEP_MS`' reason: long enough that the
 *  frame which notices the effect is never the frame after the one that
 *  dropped it, and short enough that nothing replays on a reconnect. */

export const MAX_SPECIALS = 12;
/** `MAX_CASTS`' cap. A potion has the same one-second use delay a cast has, so
 *  a correct session cannot fill this; an incorrect one is bounded. */

export interface SpecialEffectMark {
  readonly key: number;
  readonly entityId: number;
  /** The original's `SPECIAL_EFFECT` ordinal (`length.h:635-643`), passed
   *  through untranslated. Which particle system that names is the renderer's
   *  table, not this module's. */
  readonly effect: number;
  readonly bornMs: number;
}

/** One `PointChange` the renderer turns into its point effect, if the point
 *  has one (`ShowPointEffect`, `PythonCharacterManager.cpp:234-248`). */
export interface PointEffectMark {
  readonly key: number;
  readonly entityId: number;
  /** `EPointTypes` ordinal (`char.h:99-100`), untranslated. */
  readonly point: number;
  readonly bornMs: number;
}

/** One body this client saw stunned (`stunsHere`), which the renderer hangs
 *  `EFFECT_STUN` on. */
export interface StunMark {
  readonly key: number;
  readonly entityId: number;
  readonly bornMs: number;
}

// ------------------------------------------------------------------------- log

export const MAX_LOG_LINES = 5;

/** The last few lines of the fight. A ring, not a transcript: a scrollback is a
 *  feature with a UI, and this is a corner of the HUD. */
export class CombatLog {
  private items: string[] = [];

  push(line: string): void {
    this.items.push(line);
    while (this.items.length > MAX_LOG_LINES) this.items.shift();
  }

  get lines(): readonly string[] {
    return this.items;
  }

  clear(): void {
    this.items = [];
  }
}

/** Who did what to whom, in one line. `you` for the local player on both sides. */
export function describeCombat(
  event: CombatResolution,
  outcome: CombatOutcome,
  selfId: number,
  nameOf: (id: number) => string,
): string {
  const attacker = event.attackerId === selfId ? "you" : nameOf(event.attackerId);
  const target = event.targetId === selfId ? "you" : nameOf(event.targetId);

  if (outcome.missed) return `${attacker} missed ${target}`;
  const verb = outcome.critical ? "crit" : outcome.blocked ? "grazed" : "hit";
  const killed = outcome.killed ? " — killed" : "";
  return `${attacker} ${verb} ${target} for ${event.damage}${killed}`;
}

// ------------------------------------------------------------------------ feed

/** What the feed needs to know about the world to turn an event into an effect. */
export interface CombatContext {
  readonly selfId: number;
  nameOf(id: number): string;
  /** Where an entity is right now, or null if it is not being drawn. */
  positionOf(id: number): { readonly xMm: number; readonly yMm: number } | null;
}

/** The read-only half, which is all the renderer is given. */
export interface CombatView {
  /** The numbers, and nothing else. See `Impact` for the other half. */
  readonly floaters: readonly Floater[];
  /** Blows landing: the flash, the shake, the ground mark, the burst, the
   *  flinch. Raised locally at the attacker's own hit frame where this client
   *  swung, and off the server's event for a blow it did not predict. */
  readonly impacts: readonly Impact[];
  readonly deaths: readonly DeathMark[];
  readonly swings: readonly Swing[];
  readonly casts: readonly SkillCast[];
  /** Canned effects the server named. The other four members of this view are
   *  the client's own reading of events it can already see; this one is the
   *  only thing here the server asks for in so many words, because drinking a
   *  potion leaves no other trace on the wire a client could read. */
  readonly specials: readonly SpecialEffectMark[];
  /** Points the server said changed around a body: a level-up or one of its quarters. */
  readonly points: readonly PointEffectMark[];
  readonly stuns: readonly StunMark[];
  flashAt(entityId: number, nowMs: number): number;
}

export class CombatFeed implements CombatView {
  private readonly floaterField = new FloaterField();
  private readonly flashField = new FlashField();
  private impactMarks: Impact[] = [];
  private nextImpactKey = 0;
  /** See `presentsOwnBlows`. Null until this client has swung once. */
  private localPresenter: number | null = null;
  private deathMarks: DeathMark[] = [];
  private nextDeathKey = 0;
  private swingMarks: Swing[] = [];
  private nextSwingKey = 0;
  private castMarks: SkillCast[] = [];
  private nextCastKey = 0;
  private specialMarks: SpecialEffectMark[] = [];
  private nextSpecialKey = 0;
  private pointMarks: PointEffectMark[] = [];
  private stunMarks: StunMark[] = [];
  readonly log = new CombatLog();

  record(event: CombatResolution, context: CombatContext, nowMs: number): void {
    if (event.targetId === 0) {
      // A combo step swung at nobody: the mover's `FUNC_COMBO` move, which
      // observers play and nothing more (`InstanceBase.cpp:1394-1420`).
      if (shouldPresentRemoteNormalAttack(event, context.selfId)) {
        this.swing(event.attackerId, nowMs, undefined, undefined, event.comboMotion);
      }
      return;
    }
    const outcome = decodeCombatFlags(event.flags);
    const at = context.positionOf(event.targetId);

    // No position means the target is outside what this client is being sent, so
    // there is nowhere to put the number. The log line still goes in: the player
    // is entitled to know a party member is being chewed on off-screen.
    if (at !== null) {
      this.floaterField.spawn(
        {
          entityId: event.targetId,
          attackerId: event.attackerId,
          xMm: at.xMm,
          yMm: at.yMm,
          text: floaterTextFor(event.damage, outcome),
          style: floaterStyleFor(outcome),
          incoming: event.targetId === context.selfId,
        },
        nowMs,
      );
    }

    if (!outcome.missed && at !== null && !this.locallyPresented(event.attackerId)) {
      this.impact(
        {
          entityId: event.targetId,
          attackerId: event.attackerId,
          xMm: at.xMm,
          yMm: at.yMm,
          style: floaterStyleFor(outcome),
          skill: event.skill ?? 0,
        },
        nowMs,
        false,
      );
    }

    if (shouldPresentRemoteNormalAttack(event, context.selfId)) {
      this.swing(event.attackerId, nowMs, undefined, undefined, event.comboMotion);
    }

    if (fallsHere(outcome, event.targetId, context.selfId)) {
      if (at !== null) {
        this.deathMarks.push({
          key: this.nextDeathKey,
          entityId: event.targetId,
          xMm: at.xMm,
          yMm: at.yMm,
          bornMs: nowMs,
        });
        this.nextDeathKey += 1;
        while (this.deathMarks.length > MAX_DEATHS) this.deathMarks.shift();
      }
    }

    if (stunsHere(outcome, event.targetId, context.selfId)) {
      this.stunMarks.push({ key: this.nextSpecialKey, entityId: event.targetId, bornMs: nowMs });
      this.nextSpecialKey += 1;
      while (this.stunMarks.length > MAX_SPECIALS) this.stunMarks.shift();
    }

    this.log.push(describeCombat(event, outcome, context.selfId, context.nameOf));
  }

  impact(spec: ImpactSpec, nowMs: number, local = true): Impact {
    const mark: Impact = { ...spec, key: this.nextImpactKey, bornMs: nowMs, local };
    this.nextImpactKey += 1;
    this.impactMarks.push(mark);
    // Drop the oldest: the blow that just landed is the one being watched.
    while (this.impactMarks.length > MAX_IMPACTS) this.impactMarks.shift();
    this.flashField.hit(spec.entityId, nowMs);
    return mark;
  }

  presentsOwnBlows(attackerId: number | null): void {
    this.localPresenter = attackerId;
  }

  /** Whether this attacker's blows are presented by this client's own hit
   *  frame rather than by the server's event. Read by `record`, and by
   *  `main.ts`'s sound sink for the same division. */
  locallyPresented(attackerId: number): boolean {
    return this.localPresenter !== null && attackerId === this.localPresenter;
  }

  swing(entityId: number, nowMs: number, step?: number, clip?: ClipName, motion?: number): void {
    this.swingMarks.push(step === undefined
      ? { key: this.nextSwingKey, entityId, bornMs: nowMs, ...(motion ? { motion } : {}) }
      : { key: this.nextSwingKey, entityId, bornMs: nowMs, step, ...(clip === undefined ? {} : { clip }) });
    this.nextSwingKey += 1;
    while (this.swingMarks.length > MAX_SWINGS) this.swingMarks.shift();
  }

  cast(spec: SkillCastSpec, nowMs: number): SkillCast {
    const mark: SkillCast = {
      casterId: spec.casterId,
      vnum: spec.vnum,
      xMm: spec.xMm,
      yMm: spec.yMm,
      headingRad: spec.headingRad ?? 0,
      key: this.nextCastKey,
      bornMs: nowMs,
    };
    this.nextCastKey += 1;
    this.castMarks.push(mark);
    // Drop the oldest: the cast that just happened is the one being watched.
    while (this.castMarks.length > MAX_CASTS) this.castMarks.shift();
    return mark;
  }

  special(entityId: number, effect: number, nowMs: number): SpecialEffectMark {
    const mark: SpecialEffectMark = {
      entityId,
      effect,
      key: this.nextSpecialKey,
      bornMs: nowMs,
    };
    this.nextSpecialKey += 1;
    this.specialMarks.push(mark);
    while (this.specialMarks.length > MAX_SPECIALS) this.specialMarks.shift();
    return mark;
  }

  point(entityId: number, point: number, nowMs: number): PointEffectMark {
    const mark: PointEffectMark = { entityId, point, key: this.nextSpecialKey, bornMs: nowMs };
    this.nextSpecialKey += 1;
    this.pointMarks.push(mark);
    while (this.pointMarks.length > MAX_SPECIALS) this.pointMarks.shift();
    return mark;
  }

  /** Expire everything that has run its course. Once per frame, before drawing. */
  prune(nowMs: number): void {
    this.floaterField.prune(nowMs);
    this.flashField.prune(nowMs);
    this.impactMarks = this.impactMarks.filter((mark) => nowMs - mark.bornMs < IMPACT_KEEP_MS);
    this.deathMarks = this.deathMarks.filter((mark) => nowMs - mark.bornMs < DEATH_EFFECT_MS);
    this.swingMarks = this.swingMarks.filter((swing) => nowMs - swing.bornMs < SWING_KEEP_MS);
    this.castMarks = this.castMarks.filter((mark) => nowMs - mark.bornMs < CAST_KEEP_MS);
    this.specialMarks = this.specialMarks.filter(
      (mark) => nowMs - mark.bornMs < SPECIAL_KEEP_MS,
    );
    this.pointMarks = this.pointMarks.filter((mark) => nowMs - mark.bornMs < SPECIAL_KEEP_MS);
    this.stunMarks = this.stunMarks.filter((mark) => nowMs - mark.bornMs < SPECIAL_KEEP_MS);
  }

  get floaters(): readonly Floater[] {
    return this.floaterField.all;
  }

  get impacts(): readonly Impact[] {
    return this.impactMarks;
  }

  get deaths(): readonly DeathMark[] {
    return this.deathMarks;
  }

  get swings(): readonly Swing[] {
    return this.swingMarks;
  }

  get casts(): readonly SkillCast[] {
    return this.castMarks;
  }

  get specials(): readonly SpecialEffectMark[] {
    return this.specialMarks;
  }

  get points(): readonly PointEffectMark[] {
    return this.pointMarks;
  }

  get stuns(): readonly StunMark[] {
    return this.stunMarks;
  }

  flashAt(entityId: number, nowMs: number): number {
    return this.flashField.intensity(entityId, nowMs);
  }

  /** A reconnect starts a new world; carrying floaters across it would draw
   *  numbers over entities that have been renumbered. */
  clear(): void {
    this.floaterField.clear();
    this.flashField.clear();
    this.impactMarks = [];
    this.localPresenter = null;
    this.deathMarks = [];
    this.swingMarks = [];
    this.castMarks = [];
    this.specialMarks = [];
    this.pointMarks = [];
    this.stunMarks = [];
    this.log.clear();
  }
}

// ------------------------------------------------------------------- attacking

export class AttackRepeater {
  private heldSince: number | null = null;
  private lastSentMs = -Infinity;

  press(nowMs: number): void {
    if (this.heldSince !== null) return;
    this.heldSince = nowMs;
  }

  release(): void {
    this.heldSince = null;
  }

  get held(): boolean {
    return this.heldSince !== null;
  }

  spend(nowMs: number): void {
    this.lastSentMs = nowMs;
  }

  /** True at most once per `intervalMs` while held. Call every beat. */
  poll(nowMs: number, intervalMs: number): boolean {
    if (this.heldSince === null) return false;
    if (nowMs - this.lastSentMs < intervalMs) return false;
    this.lastSentMs = nowMs;
    return true;
  }

  readiness(nowMs: number, intervalMs: number): number {
    const since = nowMs - this.lastSentMs;
    if (!Number.isFinite(since)) return 1;
    return since >= intervalMs ? 1 : Math.max(0, since / intervalMs);
  }
}

// ---------------------------------------------------------------- swing lock

export const REFERENCE_SWING_MS = 1_000;

export function cooldownTicksFor(attackSpeed: number): number {
  if (attackSpeed <= 0) return cooldownTicksFor(100);
  return Math.max(1, Math.floor((calculateDuration(attackSpeed) * TICK_HZ + 500) / 1000));
}

export function swingIntervalMs(attackSpeed: number): number {
  return cooldownTicksFor(attackSpeed) * TICK_MS;
}

export function clickBecomesSwing(
  body: { readonly kind: number; readonly hp: number; readonly maxHp: number } | null,
  awayMm: number,
  reachMm: number,
  canAct: boolean,
): boolean {
  return body !== null && isAttackable(body) && canAct && awayMm <= reachMm;
}

export function swingLockTicksFor(clipDurationMs: number, attackSpeed: number): number {
  const ratio = attackSpeed > 0 ? attackSpeed / 100 : 1;
  return ticksForMs(clipDurationMs / ratio);
}

export function ticksForMs(intervalMs: number): number {
  return Math.max(1, Math.ceil(intervalMs / TICK_MS));
}

function calculateDuration(attackSpeed: number): number {
  const shortfall = 100 - attackSpeed;
  const percent = shortfall > 0
    ? 100 + shortfall
    : shortfall < 0
      ? Math.floor(10_000 / attackSpeed)
      : 100;
  return Math.floor((REFERENCE_SWING_MS * percent) / 100);
}

export class SwingLock {
  private tick = 0;
  private unlockTick = 0;
  private armTick = 0;

  /** Whether the tick about to be produced falls inside the current swing. */
  get locked(): boolean {
    return this.tick < this.unlockTick;
  }

  get dueForSwing(): boolean {
    return this.tick >= this.armTick;
  }

  swing(cooldownTicks: number, nextSwingTicks?: number): void {
    this.unlockTick = this.tick + cooldownTicks;
    if (nextSwingTicks === undefined) return;
    this.armTick = this.tick + nextSwingTicks;
    this.unlockTick = Math.max(this.unlockTick, this.armTick);
  }

  /** Call exactly once per input frame, after reading `locked` for that
   *  frame -- never before, or the frame that starts the swing would read
   *  itself as already inside it. */
  advance(): void {
    this.tick += 1;
  }
}

export function heldKeyReservesApproach(
  selected:
    | { readonly id: number; readonly kind: number; readonly hp: number; readonly maxHp: number }
    | null,
  quarry: { readonly id: number } | null,
  canAct: boolean,
): number | null {
  if (!canAct) return null;
  // Something is already in reach: that is the swing, and it happens now.
  if (quarry !== null) return null;
  // Nothing selected, or a corpse: `__GetAliveTargetInstancePtr` answers null
  // and `NEW_Attack` swings into the character's own facing rather than
  // walking anywhere (`PythonPlayerInput.cpp:598-623`).
  if (selected === null || !isAttackable(selected)) return null;
  return selected.id;
}
